import React, { useEffect, useState } from 'react';
import {
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Fab,
	Grid,
	IconButton,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CollectionsIcon from '@mui/icons-material/Collections';
import { DialogFoto } from './DialogFoto';
import { AlertSuccess } from '../../utils/componentes-utiles/AlertError';
import { deleteFoto, getFotos, postComentarioFoto, toggleLikeFoto } from '../../services/apis';
import { getCookie } from '../../utils/cookies';
import { ImagenPrivada } from '../../utils/ImagenPrivada';

const inicialUsuario = (nombre = '') => nombre.trim().charAt(0).toUpperCase() || '?';

const formatearFecha = (valor) => {
	if (!valor) return '';
	return new Date(valor).toLocaleDateString('ca-ES', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
	});
};

export const Fototeca = () => {
	const [dialogSubirFoto, setDialogSubirFoto] = useState(false);
	const [alertSucces, setAlertSucces] = useState(false);
	const [fotos, setFotos] = useState([]);
	const [comentariosInput, setComentariosInput] = useState({});
	const [comentariosVisible, setComentariosVisible] = useState({});
	const [showFab, setShowFab] = useState(false);
	const [loading, setLoading] = useState(false);
	const [fotoEliminar, setFotoEliminar] = useState(null);

	const cargarFotos = async () => {
		try {
			const response = await getFotos();
			const fotosConEstado = response.data.map((foto) => ({
				...foto,
				comentarios: foto.comentarios || [],
			}));
			setFotos(fotosConEstado);
		} catch (error) {
			console.log('Error en carregar fotos:', error);
		}
	};

	const eliminarFoto = async () => {
		if (!fotoEliminar) return;

		setLoading(true);
		try {
			const response = await deleteFoto(fotoEliminar.id);
			if (response.data.success === true) {
				await cargarFotos();
				setFotoEliminar(null);
			}
		} catch (error) {
			console.error('Error en eliminar la foto:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		cargarFotos();

		const handleScroll = () => {
			setShowFab(window.scrollY > 100);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const toggleComentarios = (id_foto) => {
		setComentariosVisible((prev) => ({ ...prev, [id_foto]: !prev[id_foto] }));
	};

	const handleComentarioChange = (id_foto, value) => {
		setComentariosInput((prev) => ({ ...prev, [id_foto]: value }));
	};

	const handleEnviarComentario = async (id_foto) => {
		const texto = comentariosInput[id_foto]?.trim();
		if (!texto) return;
		try {
			const token = getCookie('PHPSESSID');
			await postComentarioFoto({ id_foto, comentario: texto, token });
			await cargarFotos();
			setComentariosInput((prev) => ({ ...prev, [id_foto]: '' }));
			setComentariosVisible((prev) => ({ ...prev, [id_foto]: true }));
		} catch (error) {
			console.log('Error en enviar el comentari', error);
		}
	};

	const handleToggleLike = async (foto) => {
		try {
			const token = getCookie('PHPSESSID');
			await toggleLikeFoto({ id_foto: foto.id, token });

			setFotos((prev) => (
				prev.map((item) => (
					item.id === foto.id
						? {
							...item,
							me_gusta: !item.me_gusta,
							total_likes: !item.me_gusta ? item.total_likes + 1 : item.total_likes - 1,
						}
						: item
				))
			));
		} catch (error) {
			console.log("Error en canviar el m'agrada", error);
		}
	};

	return (
		<Box className="photo-shell">
			<Paper elevation={0} className="photo-header">
				<Box className="photo-header-copy">
					<Typography className="photo-eyebrow">
						Fototeca
					</Typography>
					<Typography variant="h4" className="photo-title">
						Feed de fotos
					</Typography>
				</Box>
				<Button variant="contained" startIcon={<AddAPhotoIcon />} onClick={() => setDialogSubirFoto(true)}>
					Pujar foto
				</Button>
			</Paper>

			<Grid container className="photo-layout">
				<Grid size={{ xs: 12 }}>
					<Stack className="photo-feed">
						{fotos.map((foto) => {
							const comentarios = foto.comentarios || [];
							const comentariosMostrados = comentariosVisible[foto.id] ? comentarios : comentarios.slice(0, 2);

							return (
								<Card className="photo-post" elevation={0} key={foto.id}>
									<Box className="photo-post-header">
										<Avatar className="photo-avatar">
											{inicialUsuario(foto.nombre_usuario)}
										</Avatar>
										<Box className="photo-user-block">
											<Typography className="photo-user-name">
												{foto.nombre_usuario}
											</Typography>
											{foto.fecha && (
												<Typography className="photo-date">
													{formatearFecha(foto.fecha)}
												</Typography>
											)}
										</Box>
										{foto.opciones === 1 ? (
											<IconButton className="photo-icon-button" onClick={() => setFotoEliminar(foto)}>
												<DeleteForeverIcon />
											</IconButton>
										) : (
											<IconButton className="photo-icon-button">
												<MoreHorizIcon />
											</IconButton>
										)}
									</Box>

									<Box className="photo-image-frame">
										<ImagenPrivada
											path={foto.imagen}
											alt={foto.descripcion || `Foto ${foto.id}`}
											className="photo-image"
										/>
									</Box>

									<CardContent className="photo-post-body">
										<Box className="photo-actions">
											<Box className="photo-actions-left">
												<IconButton className="photo-action-button" onClick={() => handleToggleLike(foto)}>
													{foto.me_gusta ? <FavoriteIcon className="photo-liked" /> : <FavoriteBorderIcon />}
												</IconButton>
												<IconButton className="photo-action-button" onClick={() => toggleComentarios(foto.id)}>
													<ModeCommentOutlinedIcon />
												</IconButton>
											</Box>
											<Typography className="photo-comment-count" onClick={() => toggleComentarios(foto.id)}>
												{comentarios.length} comentaris
											</Typography>
										</Box>

										<Typography className="photo-likes">
											{foto.total_likes} M'agrada
										</Typography>

										{foto.descripcion && (
											<Typography className="photo-caption">
												<strong>{foto.nombre_usuario}</strong> {foto.descripcion}
											</Typography>
										)}

										{comentarios.length > 2 && !comentariosVisible[foto.id] && (
											<Button className="photo-view-comments" onClick={() => toggleComentarios(foto.id)}>
												Veure els {comentarios.length} comentaris
											</Button>
										)}

										{comentariosMostrados.length > 0 && (
											<Stack className="photo-comments">
												{comentariosMostrados.map((comentario) => (
													<Typography key={comentario.id} className="photo-comment">
														<strong>{comentario.nombre_usuario}</strong> {comentario.comentario}
													</Typography>
												))}
											</Stack>
										)}

										<Box className="photo-comment-form">
											<TextField
												size="small"
												placeholder="Afig un comentari..."
												fullWidth
												variant="standard"
												value={comentariosInput[foto.id] || ''}
												onChange={(event) => handleComentarioChange(foto.id, event.target.value)}
											/>
											<IconButton className="photo-send-button" onClick={() => handleEnviarComentario(foto.id)}>
												<SendRoundedIcon />
											</IconButton>
										</Box>
									</CardContent>
								</Card>
							);
						})}

						{fotos.length === 0 && (
							<Paper elevation={0} className="photo-empty">
								<CollectionsIcon />
								<Typography>Encara no hi ha fotos.</Typography>
							</Paper>
						)}
					</Stack>
				</Grid>
			</Grid>

			{showFab && (
				<Fab
					color="primary"
					aria-label="Pujar foto"
					className="photo-floating-fab"
					onClick={() => setDialogSubirFoto(true)}
				>
					<AddAPhotoIcon />
				</Fab>
			)}

			{dialogSubirFoto && (
				<DialogFoto
					open={dialogSubirFoto}
					onClose={() => setDialogSubirFoto(false)}
					year={new Date().getFullYear()}
					cargarFotos={cargarFotos}
				/>
			)}

			{loading && (
				<Box className="photo-loading-overlay">
					<CircularProgress color="inherit" />
				</Box>
			)}

			<Dialog open={Boolean(fotoEliminar)} onClose={() => setFotoEliminar(null)} maxWidth="xs" fullWidth>
				<DialogTitle className="photo-delete-title">
					<DeleteForeverIcon />
					Eliminar foto
				</DialogTitle>
				<DialogContent dividers>
					<Typography className="photo-delete-copy">
						Aquesta acció ocultarà la foto de la fototeca. No s'esborraran els comentaris ni els registres associats.
					</Typography>
				</DialogContent>
				<DialogActions className="photo-delete-actions">
					<Button onClick={() => setFotoEliminar(null)} disabled={loading}>
						Cancel·lar
					</Button>
					<Button color="error" variant="contained" startIcon={<DeleteForeverIcon />} onClick={eliminarFoto} disabled={loading}>
						{loading ? 'Eliminant...' : 'Eliminar'}
					</Button>
				</DialogActions>
			</Dialog>

			{alertSucces && (
				<AlertSuccess
					open={alertSucces}
					setOpen={setAlertSucces}
					titulo="Foto pujada"
					mensaje=""
				/>
			)}
		</Box>
	);
};
